import React from 'react'

import { SIDEBAR_MINIMIZED_WIDTH, SIDEBAR_WIDTH } from 'config'
import { SearchIcon } from 'lucide-react'

import { ListItem, ListItemButton, ListItemIcon, ListItemText, Box } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { KeyChip } from 'components/display'

import { useShortcutBindings } from 'hooks'
import { ariaBinding, formatBinding } from 'utils'

interface Props {
  sidebarOpen?: boolean
  /** Opens the search / Ask overlay. */
  onOpenSearch?: () => void
}

/**
 * The sidebar's search entry point, sitting above the nav list.
 *
 * It is built as a NAV ROW, not as a search field. An earlier version wore input
 * chrome — border, fill, field padding — on the theory that "you can search here"
 * looks like a box. That was wrong twice over: it impersonated an `<input>` that
 * never accepts typing (the query belongs to the overlay), and its own padding and
 * border gave the icon a different horizontal inset from every nav icon below it,
 * so the column of icons visibly stepped. Sharing `SidebarList`'s exact row
 * structure makes the icon align and the row centre vertically BY CONSTRUCTION
 * rather than by hand-tuned padding that drifts the next time the list changes.
 *
 * `ListItemText` already carries `justifyContent: 'space-between'` at full width,
 * so the shortcut hint sits in the slot the row shape was already built for.
 *
 * The hint is read from the shared shortcut catalog, not from a literal here, so a
 * user who rebinds Search in Settings sees this row change with it. An advertised
 * chord that is a second copy of the handler's chord is a lie waiting to happen
 * the first time either one moves.
 *
 * The handler SHIPPED (VRG-227). `Sidebar` wires this row to `useSearchStore`'s
 * `openSearch` and `useSearchHotkey` binds the chord, so the hint now describes a
 * binding that actually fires. The caveat that used to sit here — "the ⌘K handler
 * is still VRG-227's to write, `handleOpenSearch` is a TODO stub" — outlived the
 * stub by long enough to mislead a docs sweep into publishing "search is not built
 * yet" to customers, which is why it is corrected here rather than deleted: the
 * catalog-not-a-literal reasoning above is what kept the row and the handler from
 * drifting apart once the handler landed.
 *
 * Placement is the sidebar rather than the page `Header`: `Header` is page-scoped
 * (breadcrumb + title + one primary action), so global search there would be
 * global chrome nested inside page chrome.
 */
export const SidebarSearchTrigger = ({ sidebarOpen, onOpenSearch }: Props) => {
  const theme = useTheme()
  const { search } = useShortcutBindings()

  // pb 17px — 16px of PAINTED air between this row's pill and the nav container's top
  // rule, matching the 16px between the logo header's rule and the collapse row's pill
  // above. The two rules sandwiching this block are the same rule seen twice, so an even
  // gap either side is what makes them read as a pair rather than as one that slipped.
  //
  // 17 AND NOT 16, which is the whole reason this is a pixel value rather than `pb: 4`.
  // The nav container carries `mt: '-1px'` (Sidebar.tsx, and deliberately — it gives back
  // the pixel its always-reserved transparent border takes, so the first nav row sits the
  // same distance from Search as any two rows sit from each other). That pulls the rule up
  // onto the last pixel of this padding, so 16px of box paints 15px of air. Measured at
  // 1280x800 with the list scrolled so the rule is lit: pill bottom 208, rule band
  // [223,224) -> 15px, against 16px above. `pb: '3px'` on that same container is the same
  // species of value for the same reason.
  //
  // pb:0 was the previous value, on a comment claiming "8px above, 8px below" via the nav
  // <List>'s own pt:1. That was wrong in the way a reasoned-about number can be and a
  // measured one cannot: the List's padding is INSIDE the scroll container, below the
  // rule, so it buys this row nothing. The pill sat flush against the rule. Measure a gap
  // that spans a border; do not derive it.
  return (
    <ListItem sx={{ pt: 2, pb: '17px', pr: 3, pl: 3, textDecoration: 'none' }}>
      <ListItemButton
        aria-label="Search"
        aria-keyshortcuts={ariaBinding(search)}
        disableRipple
        onClick={onOpenSearch}
        sx={[
          {
            borderRadius: 2,
            pt: 3,
            pb: 3,
            // pr:1, not the nav rows' pr:4. These two rows are the only ones with a
            // TRAILING element; at pr:4 the chip sat 28px off the edge, mirroring the
            // 28px icon inset on the left but crowding the label. Symmetry is not the
            // goal when only one side carries content — the chip moves right and the
            // label gets the room.
            pr: 1,
            // Pinned to the nav rows' 48px. These two rows carry a KeyChip (and the
            // Collapse row a lock button) which are taller than the 24px icon, so
            // without this the row grows only when EXPANDED — shifting every nav row
            // below it down the moment the sidebar opens. Height must not depend on
            // what the row happens to contain.
            height: 48,
          },
          sidebarOpen
            ? { minWidth: SIDEBAR_WIDTH - 24, justifyContent: 'initial' }
            : { maxWidth: SIDEBAR_MINIMIZED_WIDTH - 24 },
        ]}
      >
        <ListItemIcon
          sx={[
            { color: theme.palette.primary[900], minWidth: 0, justifyContent: 'center' },
            sidebarOpen ? { mr: 0 } : { mr: 'auto' },
          ]}
        >
          <SearchIcon strokeWidth={1} />
        </ListItemIcon>
        <ListItemText
          disableTypography
          sx={[
            {
              fontSize: 16,
              fontFamily: 'Lato',
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'center',
              marginLeft: 3,
              // Nav rows sit inside a <Link> that collapses line-height to 1; this row
              // has no Link, so it would otherwise inherit 1.4 and render 6px taller
              // than every row beneath it. Pinned so the whole column is one rhythm.
              lineHeight: 1,
              // ListItemText's default 4px margins + the KeyChip would make this
              // block taller than the 24px icon and grow the row on expand.
              my: 0,
            },
            sidebarOpen ? { opacity: 1, display: 'flex' } : { opacity: 0, display: 'none' },
          ]}
        >
          Search
          {/* The SHARED chip — same treatment as the Help popover and the
              shortcuts dialog. It was a bespoke 12px faint span here, which is
              how one surface quietly ends up with a different chord style. */}
          <KeyChip muted>{formatBinding(search)}</KeyChip>
        </ListItemText>
      </ListItemButton>
    </ListItem>
  )
}
